import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { CreateCertificateModuleDocument } from 'graphql/types';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import {
    CertificateModuleForm,
    ICertificateModuleFormProps,
} from 'training/components/forms/CertificateModuleForm';
import { IModuleCreateDrawerProps } from 'training/types';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { createModuleInCache } from 'training/utils/module';
import { getUrl } from 'route/utils/getUrl';

import { CertificateModuleSelectDrawer } from './CertificateModuleSelectDrawer';

export const CertificateModuleCreateDrawer = ({
    trainingId,
    moduleGroupId,
}: IModuleCreateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const navigate = useNavigate();
    const { showApolloError } = useApolloError();
    const [searchParams, setSearchParams] = useSearchParams();

    const routeQuery = useRouteQuery();
    const certificateIdParam = routeQuery.get('certificate') || undefined;
    const [selectedCertificateId, setSelectedCertificateId] =
        useState(certificateIdParam);

    // When a certificate is already picked the inline select drawer is gone,
    // so opening "#select" should pop the overlay variant for "Replace".
    const certificateSelectDrawerOpen =
        useHashMatch('TRAINING_MODULE_SELECT') && !!selectedCertificateId;

    const [createModule] = useMutation(CreateCertificateModuleDocument);

    useEffect(() => {
        if (!certificateIdParam) return;

        // Strip the certificate param once we've consumed it into state so the
        // URL stays clean.
        const { certificate: _certificate, ...newSearchParams } =
            Object.fromEntries(searchParams);

        setSearchParams(new URLSearchParams(newSearchParams));
    }, [certificateIdParam, searchParams, setSearchParams]);

    const handleSubmit: ICertificateModuleFormProps['onSubmit'] = async (
        values
    ) => {
        const { image, ...otherValues } = values;

        if (!otherValues.certificateId) return;

        const moduleValues = {
            ...otherValues,
            imageId: image?.id,
        };

        // When moduleGroupId is given we use that for creating the module inside the moduleGroup, otherwise create it
        // in the training which will make a new moduleGroup
        const parentId = moduleGroupId ? { moduleGroupId } : { trainingId };

        try {
            await createModule({
                variables: { module: moduleValues, ...parentId },
                update: (cache, { data }) => {
                    const { module: newModule, moduleGroup } =
                        data?.createCertificateModule || {};

                    if (!newModule || !moduleGroup) return;

                    createModuleInCache(
                        newModule,
                        moduleGroup,
                        !moduleGroupId,
                        trainingId,
                        cache
                    );
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.certificate.create'), {
            variant: 'success',
        });

        // Go back to training to close drawer
        gotoTrainingPage(navigate, trainingId);

        return;
    };

    return (
        <>
            <PageTitle>{translate('newCertificateModule')}</PageTitle>

            <CertificateModuleSelectDrawer
                open={certificateSelectDrawerOpen}
                onClose={() => {
                    // Drop the hash so the overlay closes; keep the form mounted.
                    navigate(window.location.pathname);
                }}
                onSelect={(certificate) => {
                    setSelectedCertificateId(certificate.id);
                    navigate(window.location.pathname);
                }}
            />

            {!selectedCertificateId ? (
                <CertificateModuleSelectDrawer
                    open
                    inDrawer={false}
                    onSelect={(certificate) => {
                        setSelectedCertificateId(certificate.id);
                    }}
                />
            ) : (
                <CertificateModuleForm
                    certificateId={selectedCertificateId}
                    moduleGroupId={moduleGroupId}
                    onClose={() => gotoTrainingPage(navigate, trainingId)}
                    onReplaceCertificate={() => {
                        navigate(getUrl('TRAINING_MODULE_SELECT'));
                    }}
                    onSubmit={handleSubmit}
                />
            )}
        </>
    );
};
